import { useState } from 'react';
import ReportService from '../../services/reports/Report.service';
import TipContext from '../../providers/GlobalTip';
import { BaseReportParsedData } from '../../interfaces/reports/BaseReport.interface';
import React, { useEffect, useRef } from 'react';

export interface ReportRef {
    generatePdf: () => boolean;
    generateCsv?: () => boolean;
}

interface BaseReportProps {
    title: string;
    subtitle?: string;
    tableColumns: string[];
    tableData: Array<Array<string | number>>;
    fileName: string;
    customTrend?: number | null;
    setCustomTrend?: (value: number) => void;
    trendDisplayMode?: 'editable' | 'readonly';
    trendLabel?: string;
    orientation?: 'p' | 'l';
    pageSize?: string;
    hierarchy?: string[] | null;
    setHierarchy?: (value: string[] | null) => void;
    hierarchyOptions?: Record<string, string[]>;

    // Parsed Data required for filtering
    parsedData?: BaseReportParsedData[] | null;

    // Location, class, category optional filters/dropdowns
    filteredLocationCodes?: string[] | null;
    filteredClassCodes?: string[] | null;
    filteredCategories?: string[] | null;
    setFilteredLocationCodes?: (codes: string[]) => void;
    setFilteredClassCodes?: (codes: string[]) => void;
    setFilteredCategories?: (categories: string[]) => void;
    onApplyFilters?: () => void; // Added prop for apply filter logic
    extraActionButtons?: React.ReactNode;

    hasSecondaryToolbar?: boolean; // If true, render a second toolbar row (for date range, etc.)
}

interface DropdownOption {
    value: string;
    display: string;
}

interface MultiSelectDropdownProps {
    label: string;
    options: DropdownOption[];
    selected: string[];
    setSelected: (values: string[]) => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({ label, options, selected, setSelected }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleValue = (val: string) => {
        if (selected.includes(val)) {
            setSelected(selected.filter(v => v !== val));
        } else {
            setSelected([...selected, val]);
        }
    };

    const filteredOptions = options.filter(opt => {
        const term = search.trim().toLowerCase();
        if (!term) return true;
        return opt.value.toLowerCase().includes(term) || opt.display.toLowerCase().includes(term);
    });

    // Plural label mapping for dropdowns
    const pluralLabelMap: Record<string, string> = {
        Location: 'Locations',
        Category: 'Categories',
        Class: 'Classes',
    };
    const pluralLabel = pluralLabelMap[label] || label + 's';

    const displayLabel = selected.length === 0
        ? `All ${pluralLabel}`
        : `${selected.length} ${pluralLabel}`;

    return (
        <div ref={dropdownRef} className="relative w-full">
            <div>
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className={`w-full text-left flex items-center justify-between bg-white hover:bg-[#f1f9fb] border border-[#e1edf1] hover:border-[#c8d9df] text-[13px] font-medium text-gray-700 rounded-md h-10 px-4 transition focus:outline-none ${open ? 'ring-2 ring-offset-0 ring-sky-200' : 'focus:ring-2 focus:ring-sky-200'}`}
                >
                    <span className="truncate">{displayLabel}</span>
                    <span className={`ml-2 text-sm transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
                </button>
            </div>
            {open && (
                <div className="absolute z-50 mt-1 left-0 w-[420px] max-w-[90vw] bg-white border border-[#d5e3e8] rounded-md shadow-md max-h-72 overflow-auto">
                    <div className="sticky top-0 bg-white px-3 pt-3 pb-2 border-b border-gray-100">
                        <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-sky-300"
                            placeholder={`Search ${label}`}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <ul className="py-1">
                        {filteredOptions.length === 0 && (
                            <li className="px-4 py-2 text-xs text-gray-400">No {label} found</li>
                        )}
                        {filteredOptions.map(opt => {
                            const isSel = selected.includes(opt.value);
                            return (
                                <li
                                    key={opt.value}
                                    onClick={() => toggleValue(opt.value)}
                                    className={`px-4 py-2 text-sm cursor-pointer flex items-center justify-between hover:bg-sky-50 ${isSel ? 'bg-sky-50' : ''}`}
                                >
                                    <span className="truncate">{opt.display}</span>
                                    {isSel && <span className="text-sky-600 text-xs font-semibold">✓</span>}
                                </li>
                            );
                        })}
                    </ul>
                    {selected.length > 0 && (
                        <div className="sticky bottom-0 bg-white border-t border-gray-100 flex gap-2 p-2">
                            <button
                                type="button"
                                onClick={() => setSelected([])}
                                className="flex-1 text-[11px] font-medium bg-gray-100 hover:bg-gray-200 rounded px-2 py-1"
                            >
                                Clear All
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="flex-1 text-[11px] font-medium bg-sky-600 text-white hover:bg-sky-700 rounded px-2 py-1"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const formatTrendPercentValue = (trend: number | null | undefined): string => {
    if (trend === null || trend === undefined || Number.isNaN(trend)) {
        return '';
    }

    return Math.round(trend * 100).toString();
};

const BaseReport = ({
    title,
    subtitle,
    tableColumns,
    tableData,
    fileName,
    customTrend = null,
    setCustomTrend = () => null,
    trendDisplayMode = 'editable',
    trendLabel = 'Trend Adjust %',
    orientation = 'l',
    pageSize = 'a4',
    hierarchy = null,
    setHierarchy,
    hierarchyOptions = {},
    parsedData = null,
    filteredLocationCodes = null,
    filteredClassCodes = null,
    filteredCategories = null,
    setFilteredLocationCodes = () => null,
    setFilteredClassCodes = () => null,
    setFilteredCategories = () => null,
    onApplyFilters = () => null,
    extraActionButtons = null,
    hasSecondaryToolbar = false,
}: BaseReportProps) => {
    const globalTip = TipContext.useMessage();
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingAction, setPendingAction] = useState<'pdf' | 'csv' | null>(null);
    const [textCustomTrend, setTextCustomTrend] = useState<string>(formatTrendPercentValue(customTrend));

    useEffect(() => {
        setTextCustomTrend(formatTrendPercentValue(customTrend));
    }, [customTrend]);

    const handleExport = (type: 'pdf' | 'csv') => {
        setPendingAction(type);
        setShowConfirm(true);
    };

    const handleOnBlueCustomTrend = () => {
        const val = textCustomTrend.trim();
        const num = Number(val);
        if (val === '' || isNaN(num) || num < 0 || num > 100) {
            setTextCustomTrend(formatTrendPercentValue(customTrend));
            return;
        }
        setCustomTrend && setCustomTrend(num / 100);
    };

    const handleConfirm = () => {
        setShowConfirm(false);
        const exportData = (window as any).BasicTableSortedData ?? tableData;
        if (pendingAction === 'pdf') {
            try {
                ReportService.tableToPdf({
                    columns: tableColumns,
                    data: exportData,
                    fileName,
                    title,
                    subtitle,
                    orientation,
                    pageSize
                });
            } catch (error) {
                if (globalTip) globalTip.show("Failed to generate PDF");
            }
        } else if (pendingAction === 'csv') {
            try {
                ReportService.tableToCSV({
                    columns: tableColumns,
                    data: exportData,
                    fileName
                });
            } catch (error) {
                if (globalTip) globalTip.show("Failed to export CSV");
            }
        }
        setPendingAction(null);
    };

    const handleCancel = () => {
        setShowConfirm(false);
        setPendingAction(null);
    };

    // Expose these handlers for use in parent components (e.g., ReportTable)
    (window as any).BaseReportExport = handleExport;

    // Build unique options from parsedData
    const locationOptions = parsedData
        ? Array.from(
            new Map(
                parsedData
                    .filter(d => d.locationCode)
                    .map(d => [
                        d.locationCode,
                        {
                            value: d.locationCode,
                            display: d.locationName ? `${d.locationName} (${d.locationCode})` : d.locationCode
                        }
                    ])
            ).values()
        )
        : [];
    const classOptions = parsedData
        ? Array.from(
            new Map(
                parsedData
                    .filter(d => d.classCode)
                    .map(d => [
                        d.classCode,
                        {
                            value: d.classCode,
                            display: d.className ? `${d.className} (${d.classCode})` : d.classCode
                        }
                    ])
            ).values()
        )
        : [];
    const categoryOptions = parsedData
        ? Array.from(
            new Set(
                parsedData.map(d => d.category || '').filter(Boolean)
            )
        ).map(cat => ({ value: cat, display: cat }))
        : [];

    const panelClassName = hasSecondaryToolbar
        ? "px-8 pt-6 pb-6"
        : "mb-12 px-8 pt-6 pb-6";

    return (
        <>
            {/* Filters Panel */}
            <div className={panelClassName}>
                {/* Toolbar Row */}
                <div className="grid grid-cols-6 gap-x-4 gap-y-4 items-end">
                    {/* Hierarchy */}
                    <div className="col-span-1 flex flex-col min-w-[140px]">
                        <label className="text-[13px] font-semibold text-gray-800 mb-1">Hierarchy</label>
                        {hierarchyOptions && Object.keys(hierarchyOptions).length > 0 && setHierarchy && (
                            <select
                                value={
                                    hierarchy
                                        ? Object.entries(hierarchyOptions).find(([, v]) => JSON.stringify(v) === JSON.stringify(hierarchy))?.[0] ?? ''
                                        : ''
                                }
                                onChange={e => {
                                    const selected = e.target.value;
                                    if (!selected) {
                                        setHierarchy(null);
                                    } else {
                                        setHierarchy(hierarchyOptions[selected]);
                                    }
                                }}
                                className="w-full bg-white hover:bg-[#f1f9fb] border border-[#e1edf1] hover:border-[#c8d9df] rounded-md h-10 px-4 text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                {Object.entries(hierarchyOptions).map(([label]) => (
                                    <option key={label} value={label}>{label}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Location Filter */}
                    {filteredLocationCodes !== null && parsedData && (
                        <div className="col-span-1 flex flex-col min-w-[140px]">
                            <label className="text-[13px] font-semibold text-gray-800 mb-1">Location</label>
                            <MultiSelectDropdown
                                label="Location"
                                options={locationOptions}
                                selected={filteredLocationCodes}
                                setSelected={setFilteredLocationCodes}
                            />
                        </div>
                    )}

                    {/* Category Filter */}
                    {filteredCategories !== null && parsedData && (
                        <div className="col-span-1 flex flex-col min-w-[140px]">
                            <label className="text-[13px] font-semibold text-gray-800 mb-1">Category</label>
                            <MultiSelectDropdown
                                label="Category"
                                options={categoryOptions}
                                selected={filteredCategories}
                                setSelected={setFilteredCategories}
                            />
                        </div>
                    )}

                    {/* Class Filter */}
                    {filteredClassCodes !== null && parsedData && (
                        <div className="col-span-1 flex flex-col min-w-[140px]">
                            <label className="text-[13px] font-semibold text-gray-800 mb-1">Class</label>
                            <MultiSelectDropdown
                                label="Class"
                                options={classOptions}
                                selected={filteredClassCodes}
                                setSelected={setFilteredClassCodes}
                            />
                        </div>
                    )}

                    {/* Trend display/control */}
                    {customTrend !== null && customTrend !== undefined && (
                        <div className="col-span-1 flex flex-col min-w-[120px]">
                            <label className="text-[13px] font-semibold text-gray-800 mb-1">{trendLabel}</label>
                            <div className="flex items-center">
                                {trendDisplayMode === 'readonly' ? (
                                    <div
                                        className="w-full h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 bg-gray-50 flex items-center"
                                        aria-label={trendLabel}
                                    >
                                        {formatTrendPercentValue(customTrend)}
                                    </div>
                                ) : (
                                    <input
                                        type="number"
                                        aria-label={trendLabel}
                                        value={textCustomTrend}
                                        onChange={e => setTextCustomTrend(e.target.value)}
                                        onBlur={handleOnBlueCustomTrend}
                                        className="w-full h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                        min={0}
                                        max={100}
                                    />
                                )}
                                <span className="ml-2 text-gray-500 text-[13px]">%</span>
                            </div>
                        </div>
                    )}

                    {/* Empty columns for future use */}
                    <div className="col-span-1"></div>
                </div>
                {/* Actions Row */}
                <div className="flex flex-row mt-4 gap-2 float-right">
                    {extraActionButtons}
                    <button
                        className="h-8 px-4 bg-black text-white text-[13px] font-semibold shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-300 transition rounded-lg"
                        onClick={onApplyFilters}
                    >
                        Apply Filters
                    </button>
                    <button
                        className="h-8 px-4 border border-gray-300 bg-white text-gray-800 text-[13px] font-semibold hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 transition rounded-lg"
                        onClick={() => {
                            if (filteredLocationCodes !== null) setFilteredLocationCodes([]);
                            if (filteredClassCodes !== null) setFilteredClassCodes([]);
                            if (filteredCategories !== null) setFilteredCategories([]);
                        }}
                    >
                        Reset
                    </button>
                </div>
            </div>
            {showConfirm && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white p-6 rounded shadow-lg flex flex-col items-center max-w-md">
                        <div className="mb-4 text-lg font-semibold text-center">
                            Export Report
                        </div>
                        <div className="mb-4 text-gray-700 text-sm text-center">
                            Generating your report may take up to a minute. You can continue browsing in another tab while the export is processing.<br/>
                            <span className="font-semibold">Note:</span> You must allow popups for the export to open in a new tab.
                        </div>
                        <div className="flex gap-4">
                            <button onClick={handleConfirm} className="px-4 py-2 bg-blue-600 text-white rounded">Continue</button>
                            <button onClick={handleCancel} className="px-4 py-2 bg-gray-300 text-gray-700 rounded">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BaseReport;